/**
 * BaseWidget - Foundation component for all dashboard widgets
 *
 * Provides:
 * - Consistent card styling with glassmorphism
 * - Header with title, icon, and actions
 * - Loading/error states
 * - Expand/collapse functionality
 * - Drag handle for react-grid-layout
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  IconButton,
  Tooltip,
  Skeleton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  OpenInFull as ExpandIcon,
  CloseFullscreen as CollapseIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  DragIndicator as DragIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import MuiIcon from "@mui/material/Icon";
import KDTypography from "components/KDTypography";
import { useMaterialUIController } from "context";
import { useDashboard } from "../context/DashboardContext";
import { getWidgetById } from "../config/widgetRegistry";
import WidgetFilterModal from "./WidgetFilterModal";

const getCardStyle = (dark) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: dark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
  backdropFilter: dark ? "blur(10px)" : "none",
  border: dark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.08)",
  borderRadius: "16px",
  overflow: "hidden",
  transition: "all 0.3s ease",
  boxShadow: dark ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
  "& .expand-button": {
    opacity: 0,
    transition: "opacity 0.2s ease",
  },
  "&:hover .expand-button": {
    opacity: 1,
  },
  "&:hover": {
    border: dark ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(0, 0, 0, 0.15)",
    boxShadow: dark ? "0 8px 32px rgba(0, 0, 0, 0.3)" : "0 4px 16px rgba(0, 0, 0, 0.1)",
  },
});

const getEditModeCardStyle = (dark) => ({
  ...getCardStyle(dark),
  border: "2px dashed rgba(139, 92, 246, 0.5)",
  "&:hover": {
    border: "2px dashed rgba(139, 92, 246, 0.8)",
    boxShadow: "0 8px 32px rgba(139, 92, 246, 0.2)",
  },
});

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  p: 1.5,
  pb: 1,
  minHeight: 48,
};

const contentStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  p: 1.5,
  pt: 0,
  overflow: "hidden",
};

const expandedContentStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
};

function BaseWidget({
  id,
  title,
  subtitle,
  icon: Icon,
  color = "#7C3AED",
  gradient,
  children,
  loading = false,
  error = null,
  onRefresh,
  onSettings,
  showDragHandle = false,
  className,
  headerExtra,
  noPadding = false,
  isExpanded: isExpandedProp = false,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { state, actions } = useDashboard();
  const { isEditingMode, expandedWidget } = state;

  // Theme-aware colors
  const textPrimary = darkMode ? "white" : "rgba(0, 0, 0, 0.87)";
  const textSecondary = darkMode ? "rgba(255,255,255,0.5)" : "rgba(0, 0, 0, 0.5)";
  const iconBtn = darkMode ? "rgba(255,255,255,0.6)" : "rgba(0, 0, 0, 0.4)";
  const iconBtnHover = darkMode ? "white" : "rgba(0, 0, 0, 0.87)";
  const menuBg = darkMode ? "rgba(26, 26, 46, 0.98)" : "#ffffff";
  const menuText = darkMode ? "white" : "rgba(0, 0, 0, 0.87)";
  const menuIconColor = darkMode ? "rgba(255,255,255,0.7)" : "rgba(0, 0, 0, 0.5)";
  const menuHoverBg = darkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.08)";
  const skeletonBg = darkMode ? "rgba(255,255,255,0.1)" : "rgba(0, 0, 0, 0.08)";
  const cardStyle = getCardStyle(darkMode);
  const editModeCardStyle = getEditModeCardStyle(darkMode);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const isExpanded = isExpandedProp || expandedWidget === id;

  const widgetConfig = getWidgetById(id);
  const supportsFilters = widgetConfig?.supportsFilters?.length > 0;
  const currentFilters = actions.getWidgetFilters(id);
  const hasActiveFilters = currentFilters?.creator || currentFilters?.experience;

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const handleExpand = () => {
    actions.expandWidget(id);
    handleMenuClose();
  };

  const handleCollapse = () => actions.collapseWidget();

  const handleRemove = () => {
    actions.removeWidget(id);
    handleMenuClose();
  };

  const handleRefresh = () => {
    onRefresh?.();
    handleMenuClose();
  };

  const handleOpenFilters = () => {
    setFilterModalOpen(true);
    handleMenuClose();
  };

  const handleApplyFilters = (filters) => {
    actions.setWidgetFilters(id, filters);
    setFilterModalOpen(false);
  };

  const handleSettings = () => {
    onSettings?.();
    handleMenuClose();
  };

  // Loading skeleton
  if (loading) {
    return (
      <Card sx={cardStyle} className={className}>
        <Box sx={headerStyle}>
          <Box display="flex" alignItems="center" gap={1}>
            <Skeleton variant="rounded" width={32} height={32} sx={{ bgcolor: skeletonBg }} />
            <Skeleton variant="text" width={120} sx={{ bgcolor: skeletonBg }} />
          </Box>
        </Box>
        <Box sx={contentStyle}>
          <Skeleton variant="rectangular" height="100%" sx={{ bgcolor: skeletonBg, borderRadius: 2 }} />
        </Box>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card sx={{ ...cardStyle, border: "1px solid rgba(239, 68, 68, 0.5)" }} className={className}>
        <Box sx={headerStyle}>
          <Box display="flex" alignItems="center" gap={1}>
            {Icon && (
              <Box
                sx={{
                  background: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)",
                  borderRadius: "8px",
                  p: 0.75,
                  display: "flex",
                }}
              >
                {typeof Icon === "string" ? (
                  <MuiIcon sx={{ color: "white", fontSize: 18 }}>{Icon}</MuiIcon>
                ) : (
                  <Icon sx={{ color: "white", fontSize: 18 }} />
                )}
              </Box>
            )}
            <KDTypography variant="subtitle2" sx={{ color: textPrimary }} fontWeight="medium">
              {title}
            </KDTypography>
          </Box>
          {onRefresh && (
            <Tooltip title="Retry">
              <IconButton size="small" onClick={handleRefresh} sx={{ color: iconBtn }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ ...contentStyle, justifyContent: "center", alignItems: "center" }}>
          <KDTypography variant="caption" color="error">
            {error.message || "Failed to load data"}
          </KDTypography>
        </Box>
      </Card>
    );
  }

  // Expanded renders content only
  if (isExpandedProp) {
    return <Box sx={expandedContentStyle}>{children}</Box>;
  }

  return (
    <Card sx={isEditingMode ? editModeCardStyle : cardStyle} className={className}>
      {/* Header */}
      <Box sx={headerStyle}>
        <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
          {/* Drag handle */}
          {isEditingMode && showDragHandle && (
            <Box
              className="widget-drag-handle"
              sx={{
                cursor: "grab",
                color: iconBtn,
                display: "flex",
                "&:active": { cursor: "grabbing" },
              }}
            >
              <DragIcon fontSize="small" />
            </Box>
          )}

          {/* Icon */}
          {Icon && (
            <Box
              sx={{
                background: gradient || `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
                borderRadius: "8px",
                p: 0.75,
                display: "flex",
                flexShrink: 0,
              }}
            >
              {typeof Icon === "string" ? (
                <MuiIcon sx={{ color: "white", fontSize: 18 }}>{Icon}</MuiIcon>
              ) : (
                <Icon sx={{ color: "white", fontSize: 18 }} />
              )}
            </Box>
          )}

          {/* Title */}
          <Box minWidth={0} flex={1}>
            <KDTypography
              variant="subtitle2"
              fontWeight="medium"
              sx={{ color: textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {title}
            </KDTypography>
            {hasActiveFilters ? (
              <KDTypography
                variant="caption"
                sx={{
                  color: "#A78BFA",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {currentFilters.experience?.label || currentFilters.creator?.label}
              </KDTypography>
            ) : subtitle ? (
              <KDTypography
                variant="caption"
                sx={{
                  color: textSecondary,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {subtitle}
              </KDTypography>
            ) : null}
          </Box>

          {headerExtra}
        </Box>

        {/* Actions */}
        <Box display="flex" alignItems="center" gap={0.5}>
          {supportsFilters && !isExpanded && (
            <Tooltip title={hasActiveFilters ? "Filters active" : "Add filter"}>
              <IconButton
                size="small"
                onClick={handleOpenFilters}
                sx={{
                  color: hasActiveFilters ? "#7C3AED" : iconBtn,
                  bgcolor: hasActiveFilters ? "rgba(124, 58, 237, 0.2)" : "transparent",
                  "&:hover": { color: iconBtnHover, bgcolor: "rgba(139, 92, 246, 0.2)" },
                  width: 28,
                  height: 28,
                }}
              >
                <FilterIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {isExpanded ? (
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={handleCollapse}
                sx={{
                  color: iconBtnHover,
                  bgcolor: "rgba(139, 92, 246, 0.3)",
                  "&:hover": { bgcolor: "rgba(139, 92, 246, 0.5)" },
                  width: 28,
                  height: 28,
                }}
              >
                <CollapseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Expand">
              <IconButton
                className="expand-button"
                size="small"
                onClick={handleExpand}
                sx={{
                  color: iconBtn,
                  "&:hover": { color: iconBtnHover, bgcolor: "rgba(139, 92, 246, 0.2)" },
                  width: 28,
                  height: 28,
                }}
              >
                <ExpandIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {(onRefresh || onSettings || isEditingMode) && !isExpanded && (
            <Tooltip title="More options">
              <IconButton size="small" onClick={handleMenuOpen} sx={{ color: iconBtn }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              bgcolor: menuBg,
              backdropFilter: darkMode ? "blur(10px)" : "none",
              border: darkMode ? "1px solid rgba(139, 92, 246, 0.3)" : "1px solid rgba(0,0,0,0.08)",
              borderRadius: "12px",
              minWidth: 180,
              "& .MuiMenuItem-root": {
                color: menuText,
                "&:hover": { bgcolor: menuHoverBg },
              },
            },
          }}
        >
          {onRefresh && (
            <MenuItem onClick={handleRefresh}>
              <ListItemIcon>
                <RefreshIcon fontSize="small" sx={{ color: menuIconColor }} />
              </ListItemIcon>
              <ListItemText>Refresh</ListItemText>
            </MenuItem>
          )}
          {onSettings && (
            <MenuItem onClick={handleSettings}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" sx={{ color: menuIconColor }} />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
          )}
          {isEditingMode && (
            <MenuItem onClick={handleRemove} sx={{ color: "#EF4444 !important" }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: "#EF4444" }} />
              </ListItemIcon>
              <ListItemText>Remove</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Content */}
      <Box sx={noPadding ? { ...contentStyle, p: 0 } : contentStyle}>{children}</Box>

      {/* Filter Modal */}
      <WidgetFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        widgetId={id}
        currentFilters={currentFilters}
        onApply={handleApplyFilters}
      />
    </Card>
  );
}

BaseWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]),
  color: PropTypes.string,
  gradient: PropTypes.string,
  children: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.shape({ message: PropTypes.string }),
  onRefresh: PropTypes.func,
  onSettings: PropTypes.func,
  showDragHandle: PropTypes.bool,
  className: PropTypes.string,
  headerExtra: PropTypes.node,
  noPadding: PropTypes.bool,
  isExpanded: PropTypes.bool,
};

export default BaseWidget;
